import moment from "moment";

import { BillingFacturationNetResponse } from "@/Billing/Api/FacturationNet/Dto/Responses/FacNetBillLightResponse";
import { FacturationNetManager } from "@/Billing/Services/FacturationNet/FacturationNetManager";
import { ExternalBillingClaimDto } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingClaimDto";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { PortfolioNotesSortOrderType } from "@/Common/Models/UI/Enums";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ExternalBillingManager } from "@/Common/Services/ExternalBilling/ExternalBillingManager";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { MedfarBillLightPreview } from "@/Patient/Api/MedfarBilling/Dto/Responses/FacturationReponse";
import { NotesInboxTableItem } from "@/Portfolio/Models/MyNotesInboxTableItem";
import { INotesInboxModel } from "@/Portfolio/Models/UI/INotesInboxModel";
import { NotesSettingsContainer } from "./NotesSettingsContainer";
import { PortfolioSaveOptionKey } from "./PortfolioSaveOptionKey";

interface INotesInboxProps {
    Model: INotesInboxModel;
}

class NotesInbox extends React.Component<INotesInboxProps, never> {
    private helper: MyleHelper = new MyleHelper();
    private saveOptionKey = PortfolioSaveOptionKey.Notes;

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    public render(): JSX.Element {
        const columnsOptions = this.helper.loadTableColumnOptionsFromSettings(
            this.saveOptionKey,
            this.getMyleTableColumnsDefaultOptions()
        );

        const tableColumns = this.getTableColumns(columnsOptions);
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);

        const columnConfig = this.helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

        return (
            <div className="workspace">
                <CommonTableHeaderComponent
                    title={cacheStore.GetString("MyNotesLabel")}
                    searchText={this.props.Model.get("GlobalPortfolioSearchText")}
                    searchPlaceholder={this.props.Model.get("GlobalPortfolioSearchPlaceholder")}
                    onSearch={text => sessionStore.dispatch(LocalOperations.Portfolio.MyNotes_SearchBoxTextChanged, { value: text })}
                    onPrintClick={async () => await this.onPrintClick()}
                    onRefreshClick={() => sessionStore.dispatch(LocalOperations.Portfolio.MyNotes_Reload)}
                    onSettingsClick={() => sessionStore.dispatch(LocalOperations.Portfolio.MyNotes_SearchSettings_ShowOptionsButtonClick)}
                />
                <div id="posref" className="tb ws-scrollable">
                    {this.props.Model.get("ShowMyNotesDisplaySettings")
                        && <NotesSettingsContainer className="filter-component" Model={this.props.Model} />
                    }

                    <div className="myle-doc-tb main-content table-wrapper">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="NotesInbox_main_tb"
                            Columns={tableColumns}
                            DataSource={this.props.Model.get("TableData")}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            RowClickOperation={LocalOperations.Labs.Table_InboxRowClick}
                            SaveOptionskeyTarget={this.saveOptionKey}
                            ColumnSortTarget={MyleTableTarget.Myle_PortfolioMyNotes_Search_ColumnSortClick}
                            IsRowLocked={(item: any) => item.Model.IsAccessProhibited}
                            TableDataCy="portfolio-table"
                        />

                    </div>
                </div>
                <div className="ws-footer table-pager-footer">
                    <div className="row">
                        <div className="col-lg-10 col-md-9 col-sm-8">
                            <SimplePagerControl
                                PageSize={this.props.Model.get("SelectedPageSizeOption")}
                                TotalRows={this.props.Model.get("TotalQueryRows")}
                                CurrentPage={this.props.Model.get("CurrentPage")}
                                EventToDispatchOnPageChange={LocalOperations.Portfolio.MyNotes_Search_CurrentPageChange}
                            />
                        </div>
                    </div>
                </div>
                {this.props.Model.get("ColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={columnConfig}
                        SaveOptionskeyTarget={this.saveOptionKey}
                        ContainerId="columnSettingsPopup"
                        CloseColumOrder={LocalOperations.Portfolio.MyNotes_SearchSettings_ColumnOrderingPopup_Close}
                    />
                )}
            </div>
        );
    }

    private getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        const cols: MyleTableColumnOptions[] = [];

        cols.push(new MyleTableColumnOptions({
            HeaderText: cacheStore.GetString("PatientLabel"),
            FieldName: "NotesPatient",
            WidthPercentageDefault: 8,
            IsSortable: true,
            SortColumn: PortfolioNotesSortOrderType.Patient,
            SortDirection: this.props.Model.get("OrderByDirection"),
            IsSortActive: this.props.Model.get("OrderByColumn") === PortfolioNotesSortOrderType.Patient
        }));

        cols.push(new MyleTableColumnOptions({
            HeaderText: cacheStore.GetString("DateLabel"),
            FieldName: "NotesDate",
            WidthPercentageDefault: 8,
            IsSortable: true,
            SortColumn: PortfolioNotesSortOrderType.Date,
            SortDirection: this.props.Model.get("OrderByDirection"),
            IsSortActive: this.props.Model.get("OrderByColumn") === PortfolioNotesSortOrderType.Date
        }));

        cols.push(new MyleTableColumnOptions({
            HeaderText: cacheStore.GetString("ReasonLabel"),
            FieldName: "NotesReason",
            WidthPercentageDefault: 8,
            IsSortable: true,
            SortColumn: PortfolioNotesSortOrderType.Reason,
            SortDirection: this.props.Model.get("OrderByDirection"),
            IsSortActive: this.props.Model.get("OrderByColumn") === PortfolioNotesSortOrderType.Reason
        }));

        cols.push(new MyleTableColumnOptions({
            HeaderText: cacheStore.GetString("FirstAidDoctorsLabel"),
            FieldName: "NotesDoctors",
            WidthPercentageDefault: 8
        }));

        cols.push(new MyleTableColumnOptions({
            HeaderText: cacheStore.GetString("StatusLabel"),
            FieldName: "NotesStatus",
            WidthPercentageDefault: 8,
            IsSortable: true,
            SortColumn: PortfolioNotesSortOrderType.Status,
            SortDirection: this.props.Model.get("OrderByDirection"),
            IsSortActive: this.props.Model.get("OrderByColumn") === PortfolioNotesSortOrderType.Status
        }));

        if (sessionStore.getCurrentEmployee().isActiveSiteMexico() === false) {
            cols.push(new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("InvoiceLabel"),
                FieldName: "NotesInvoice",
                WidthPercentageDefault: 8
            }));
        }

        cols.push(new MyleTableColumnOptions({
            HeaderText: cacheStore.GetString("DiagnosisLabel"),
            FieldName: "NotesDiagnosis",
            WidthPercentageDefault: 8
        }));

        cols.push(new MyleTableColumnOptions({
            HeaderText: cacheStore.GetString("HealthCard"),
            FieldName: "NotesHealthCard",
            WidthPercentageDefault: 8,
            IsSortable: true,
            SortColumn: PortfolioNotesSortOrderType.NAM,
            SortDirection: this.props.Model.get("OrderByDirection"),
            IsSortActive: this.props.Model.get("OrderByColumn") === PortfolioNotesSortOrderType.NAM
        }));

        return cols;
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        const blockedLabel = cacheStore.GetString("Allowlist_BlockedLabel");
        return MyleTableColumn.buildList<NotesInboxTableItem>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "NotesPatient":
                        return {
                            renderCell: item => <div>{item.Model.IsAccessProhibited ? blockedLabel : (<a onClick={() => this.onPatientClick(item)}>{item.Model.PatientName}</a>)}</div>
                        };

                    case "NotesDate":
                        return {
                            renderCell: item => {
                                if (!item.Model.EncounterDate)
                                    return null;

                                const date = moment(item.Model.EncounterDate).format("YYYY/MM/DD");
                                const time = moment(item.Model.EncounterDate).format("HH:mm:ss A");
                                const messageDate = `${date} ${cacheStore.GetString("AtLabel")} ${time}`;

                                return <div>{messageDate}</div>;
                            }
                        };

                    case "NotesReason":
                        return {
                            renderCell: item => <div>{item.Model.IsAccessProhibited ? blockedLabel : item.Model.VisitPurpose}</div>
                        };

                    case "NotesDoctors":
                        return {
                            renderCell: item => {
                                const doctors = ArrayHelper
                                    .Unique(item.Model.EmployeeIds)
                                    .map(
                                        employeeId => (
                                            <div key={`doc_${employeeId}`}>
                                                {cacheStore.GetCliniciansPersonsByContactFacetId(employeeId)?.NameWithPrefix}
                                            </div>
                                        )
                                    );

                                return <div>{item.Model.IsAccessProhibited ? blockedLabel : doctors}</div>;
                            }
                        };

                    case "NotesStatus":
                        return {
                            renderCell: item => (
                                <div>{item.Model.IsAccessProhibited ? blockedLabel : item.Model.Status}</div>
                            )
                        };

                    case "NotesInvoice":
                        return {
                            renderCell: item => {
                                const currentEmployee = sessionStore.getCurrentEmployee();
                                if (currentEmployee.isActiveSiteMexico())
                                    return null;

                                if (item.Model.IsAccessProhibited)
                                    return (<div>{cacheStore.GetString("Allowlist_BlockedLabel")}</div>);

                                return (
                                    <div>
                                        {this.renderMedfarBillList(item.Model.LightMedfarBillsList)}
                                        {this.renderFacturationNetBillsList(currentEmployee, item.Model.FacturationNetBillsList)}
                                        {this.renderExternalBillsList(currentEmployee, item.Model.ExternalBillingClaims)}
                                    </div>
                                );
                            }
                        };

                    case "NotesDiagnosis":
                        return {
                            renderCell: item => <div>{item.Model.IsAccessProhibited ? blockedLabel : item.Model.Diagnosis}</div>
                        };

                    case "NotesHealthCard":
                        return {
                            renderCell: item => <div>{item.Model.IsAccessProhibited ? blockedLabel : item.Model.HealthCardNumber}</div>
                        };
                }
            },
            false
        );
    }

    public loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        // Get The Render Options
        return Immutable.fromJS({
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        });
    }

    public onPatientClick(item: NotesInboxTableItem): void {
        const clickEvent = new LocalEvent(LocalOperations.Portfolio.MyNotes_OnPatientClick);
        clickEvent.AddItem("item", item);
        LocalEventDispatcher.dispatch(clickEvent);
    }

    public async onPrintClick(): Promise<void> {
        const columnsOptions = this.helper.loadTableColumnOptionsFromSettings(this.saveOptionKey, this.getMyleTableColumnsDefaultOptions());
        const printConfig = this.helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());
        await portfolioStore.PortfolioActions.PrintNotes(portfolioStore.NotesInboxModel, printConfig);
    }

    private renderMedfarBillList(bills: MedfarBillLightPreview[]): JSX.Element[] {
        return (bills ?? [])
            .map(bill => (
                <a style={{ paddingRight: "5px" }} key={`medfar_${bill.FacturationRequestId}`}>
                    {bill.BillSummary}
                </a>
            ));
    }

    private renderFacturationNetBillsList(currentEmployee: EmployeeUIModel, bills: BillingFacturationNetResponse[]): JSX.Element[] {
        return (bills ?? [])
            .map(bill => (
                <div key={`facNet_${bill.ClaimId}`}>
                    {currentEmployee.ContactFacetId === bill.CreatedByEmployeeId && (
                        <a
                            style={{ paddingRight: "5px" }}
                            onClick={() => FacturationNetManager.openBill(bill.ClaimId)}
                        >
                            {BillingFacturationNetResponse.getBillDisplayString(bill)}
                        </a>
                    )}
                    {currentEmployee.ContactFacetId !== bill.CreatedByEmployeeId && (
                        <span>
                            {BillingFacturationNetResponse.getBillDisplayString(bill)}
                        </span>
                    )}
                </div>
            ));
    }

    private renderExternalBillsList(currentEmployee: EmployeeUIModel, bills: ExternalBillingClaimDto[]): JSX.Element[] {
        return (bills ?? [])
            .map(claim => {
                const canViewClaim = ExternalBillingManager.canEmployeeViewClaimDetails(currentEmployee, claim);

                return (
                    <div key={`external_${claim.BillingProvider}_${claim.ProviderClaimId}`}>
                        {canViewClaim && (
                            <a
                                style={{ paddingRight: "5px" }}
                                onClick={async () => await this.handleOnClaimClicked(claim)}
                            >
                                {ExternalBillingManager.getDisplayText(claim)}
                            </a>
                        )}
                        {!canViewClaim && (
                            <span>
                                {ExternalBillingManager.getDisplayText(claim)}
                            </span>
                        )}
                    </div>
                );
            });
    }

    private async handleOnClaimClicked(claim: ExternalBillingClaimDto): Promise<void> {
        return ExternalBillingManager.openClaimWindowAsync(
            claim,
            () => sessionStore.dispatch(LocalOperations.Portfolio.MyNotes_Refresh_ClaimStatus, {
                providerClaimId: claim.ProviderClaimId,
                encounterId: claim.EncounterId,
                billingProvider: claim.BillingProvider
            })
        );
    }
}

export { NotesInbox };
